import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {WSPIcon} from '@mintdata-wsp/resources/icons/WSPIcon';
import {WSPIcons, WSPIconSize} from '@mintdata-wsp/resources/icons/WSPIcons';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import {Tooltip} from '@mintdata/mint-kit/impl/components/tooltip/Tooltip';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./SurfaceOutlinePanelPartWrapper.less');
const cx = classnames.bind(styles);

export interface IActionItemDesc
{
    tooltipText:string;
    icon:MDIcons;
    handler:(e:React.MouseEvent<any>) => void;
    className?:string;
}

export interface SurfaceOutlinePanelPartWrapperProps
{
    title:string | JSX.Element,
    className?:string,
    isCollapsed?:boolean;
    isInitiallyCollapsed?:boolean;
    isCollapsible?:boolean;
    leftActionItems?:Array<IActionItemDesc>
    rightActionItems?:Array<IActionItemDesc>
    onCollapseChanged?:(isCollapsed:boolean) => void;
}

@observer
export class SurfaceOutlinePanelPartWrapper extends React.Component<SurfaceOutlinePanelPartWrapperProps, any>
{
    public static defaultProps:Partial<SurfaceOutlinePanelPartWrapperProps> = {
        isInitiallyCollapsed: false,
        isCollapsible: true,
        leftActionItems: [],
        rightActionItems: []
    };

    @observable
    private _isCollapsed:boolean = false;

    constructor(props)
    {
        super(props);

        const isCollapsed:boolean = this.props.isCollapsed !== undefined
            ? this.props.isCollapsed
            : this.props.isInitiallyCollapsed;
        this._setIsCollapsed(isCollapsed);
    }

    componentDidUpdate(prevProps:Readonly<SurfaceOutlinePanelPartWrapperProps>, prevState:Readonly<any>, prevContext:any):void
    {
        if( prevProps.isCollapsed !== this.props.isCollapsed )
            this._setIsCollapsed(this.props.isCollapsed);
    }

    @action.bound
    private _setIsCollapsed(value:boolean):void
    {
        this._isCollapsed = value;
    }

    @action.bound
    private _handleCollapseClick():void
    {
        this._isCollapsed = !this._isCollapsed;

        const {onCollapseChanged} = this.props;
        if( onCollapseChanged )
            onCollapseChanged(this._isCollapsed);
    }

    private _handleActionButtonDoubleClick(event:React.MouseEvent<any>):void
    {
        event.stopPropagation();
    }

    @bind
    private _onClickIconDoc(e:React.MouseEvent<HTMLElement>, componentName?:string):void
    {
        e.stopPropagation();
        e.preventDefault();

        const {lx} = controllers.ui;

        lx.showLxFrame();

        lx.postMessage({
            source: 'designer',
            event_type: 'show_component_docs',
            has_component_name: !!componentName,
            component_name: componentName ? componentName.toLowerCase() : null,
        });
    }

    private _renderActions = ():JSX.Element =>
    {
        const {isCollapsible, leftActionItems, title} = this.props;
        let {rightActionItems} = this.props;
        const isCollapsed = this._isCollapsed;

        if( isCollapsible )
        {
            rightActionItems = [...rightActionItems, {
                tooltipText: isCollapsed ? 'More' : 'Less',
                icon: MDIcons.common.Collapse,
                handler: this._handleCollapseClick,
                className: cx(isCollapsed ? 'collapseIcon_collapsed' : 'collapseIcon_expanded')
            }];
        }

        return (
            <div className={cx('actions')}>
                {this._renderActionButtons(leftActionItems, true)}
                <div className={cx('filler')} />
                {
                    title === 'Components' &&
                    <div
                        className={cx('iconDoc')}
                        onMouseDown={(e:React.MouseEvent<HTMLElement>) => this._onClickIconDoc(e)}
                    >
                        <WSPIcon
                            name={WSPIcons.visualDesigner.spreadsheet.Help}
                            size={WSPIconSize.medium20}
                        />
                    </div>
                }
                {this._renderActionButtons(rightActionItems)}
            </div>
        );
    };

    _renderActionButtons(actionItems:Array<IActionItemDesc>, isLeft:boolean = false):Array<JSX.Element>
    {
        return actionItems.map((actionItemDesc:IActionItemDesc, index:number) => (
            <div key={index} className={cx('actionItem')}>
                <Tooltip content={actionItemDesc.tooltipText} targetClassName={cx('tooltip')}>
                    <button
                        className={cx('actionItemButton', {
                            left: isLeft,
                            [actionItemDesc.className]: !!actionItemDesc.className,
                        })}
                        onClick={actionItemDesc.handler}
                        onDoubleClick={this._handleActionButtonDoubleClick}
                    >
                        <MDIcon
                            name={actionItemDesc.icon as string}
                            size={MDIconSize.medium20}
                        />
                    </button>
                </Tooltip>
            </div>
        ));
    }

    render():JSX.Element | false | null
    {
        const {className, title, isCollapsible} = this.props;
        const isCollapsed = this._isCollapsed;

        return (
            <div className={cx('base', {[className]: !!className})}>
                <Row
                    className={cx('header', {isCollapsed})}
                    onDoubleClick={isCollapsible ? this._handleCollapseClick : null}
                >
                    <div
                        className={cx('title', {isCollapsible})}
                        onClick={isCollapsible ? this._handleCollapseClick : null}
                    >
                        {title}
                    </div>
                    {this._renderActions()}
                </Row>

                <Row className={cx('content', {isCollapsed})}>
                    {this.props.children}
                </Row>
            </div>
        );
    }
}
